import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PrivateRoute from './components/common/PrivateRoute';
import Loader from './components/common/Loader';
import ErrorBoundary from './components/common/ErrorBoundary';

// Route-level code splitting: each page (and its heavy dependencies, e.g.
// the CMS editors pulled in by AdminDashboard) is only downloaded when the
// user actually navigates to it, instead of being bundled into the initial
// chunk. Addresses the "no code splitting" finding.
const Login = lazy(() => import('./pages/Login'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const MediaLibrary = lazy(() => import('./pages/MediaLibrary'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const Stations = lazy(() => import('./pages/Stations'));

function RouteFallback() {
  return <Loader />;
}

// Wraps a lazily-loaded page in its own error boundary. Previously the only
// boundary in the app was the single one in index.tsx wrapping the entire
// router - a render error on any one page (e.g. AdminDashboard) would blank
// out the whole app, including navigation, instead of just that page.
function routeElement(label: string, Page: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <ErrorBoundary label={label}>
      <Page />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <>
    <Toaster position="top-right" reverseOrder={false} />
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={routeElement('login page', Login)} />
        <Route path="/login" element={<Navigate to="/" replace />} />

        <Route element={<PrivateRoute />}>
          <Route path="/dashboard" element={routeElement('admin dashboard', AdminDashboard)} />
          <Route path="/dashboard/:slug" element={routeElement('admin dashboard', AdminDashboard)} />
          <Route path="/media" element={routeElement('media library', MediaLibrary)} />
          <Route path="/home" element={<Navigate to="/dashboard/home" replace />} />
          <Route path="/about" element={<Navigate to="/dashboard/about" replace />} />
          <Route path="/fuel" element={<Navigate to="/dashboard/fuel" replace />} />
          <Route path="/news" element={<Navigate to="/dashboard/news" replace />} />
          <Route path="/stations" element={routeElement('stations page', Stations)} />
          <Route path="/sitemap" element={routeElement('sitemap page', Sitemap)} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </>
  );
}

export default App;
